import { computed, ref } from "vue";
import { defineStore } from 'pinia';
import axios from 'axios';
import { User } from "@/vue/types/user";
import { Org } from "@/vue/types/org";
import usePermissions from "@/vue/composables/usePermissions";
import {Staff} from "@/vue/types/staff";

export const useUserStore = defineStore('userStore', () => {
    const user = ref<User>();
    const org = ref<Org>();
    const staff = ref<Staff>();

    const { hasPermission } = usePermissions();

    const can = function(permission: string) : boolean {
        if (!user.value) return false;

        return hasPermission(user.value.can, permission);
    };

    const canViewDashboard = computed(() => {
        if (!user.value || !org.value) return false;

        const hasPermission = can("view.dashboard");
        const dashboardEnabled = org.value.settings.settings?.is_dashboard_enabled !== false;

        return hasPermission && dashboardEnabled;
    });

    const canViewAnalytics = computed(() => {
        if (!user.value || !org.value) return false;

        const hasPermission = can("view.analytics.dashboard");
        const analyticsEnabled = org.value.settings.settings?.analytics_enabled !== false;

        return hasPermission && analyticsEnabled;
    });

    const canAddExtendedSoftwareService = computed(() => {
        if (!user.value || !org.value) return false;

        const hasPermission = can("add.extendedSoftwareService");
        const ESSEnabled = org.value.settings.settings?.ess_utility === true;

        return hasPermission && ESSEnabled;
    });

    const canRecalculateCompliance = computed(() => {
        if (!user.value) return false;

        return can("recalculate.compliance");
    });

    const fetchUserInfo = async () => {
        const { data } = await axios.get("/api/auth-user");

        user.value = { ...data.data, hasPermission : can };
        staff.value = data.data?.staff;

        // @ts-ignore
        window.axios.defaults.headers.common["X-Laravel-User-Id"] = data.data.id;
    };

    const fetchOrgInfo = async () => {
        const { data } = await axios.get("/api/users/my-org");

        org.value = data;
    };

    return {
        user,
        org,
        staff,
        can,
        canViewDashboard,
        canRecalculateCompliance,
        canViewAnalytics,
        canAddExtendedSoftwareService,
        fetchUserInfo,
        fetchOrgInfo,
    };
});
